import { service } from '@ember/service'
import Component from '@glimmer/component'
import { reportingLegend as legend } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type { IntlService } from 'ember-intl'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'

export interface DrivingTestsTotalsByMonthData {
  month: string
  content_words: number
  letters_and_sounds: number
  sight_words: number
}

interface Args {
  data: DrivingTestsTotalsByMonthData[]
  title: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DrivingTestsTotalsByMonth extends Component<Signature> {
  @service intl!: IntlService

  readonly legend = legend('left', 'top', 40, 10)

  get months() {
    const months = this.args.data
    return months.map((month) => convertDateFormat(month.month, 'yyyy-MM', 'MMMM'))
  }

  get yAxis() {
    const yAxisText = this.intl.t('reporting.yAxisLabels.drivingTestDetailsTotalsByMonth')
    return {
      reversedStacks: false,
      title: {
        enabled: true,
        text: yAxisText,
      },
      allowDecimals: false,
    }
  }

  get columns(): SeriesColumnOptions[] {
    const data = this.args.data

    const sightWordsLabel = this.intl.t('driving_tests.re.categories.sight_words')
    const contentWordsLabel = this.intl.t('driving_tests.re.categories.content_words')
    const lettersAndSoundsLabel = this.intl.t('driving_tests.re.categories.letters_and_sounds')

    const sightWordsData = data.map((item) => item.sight_words)
    const contentWordsData = data.map((item) => item.content_words)
    const lettersAndSoundsData = data.map((item) => item.letters_and_sounds)

    return [
      { name: sightWordsLabel, data: sightWordsData, color: colors.oceanyBlue300, type: 'column' },
      { name: contentWordsLabel, data: contentWordsData, color: colors.purplyPink300, type: 'column' },
      { name: lettersAndSoundsLabel, data: lettersAndSoundsData, color: colors.forestGreen300, type: 'column' },
    ]
  }

  <template>
    <div data-test-giraffe-driving-tests-totals-by-month class="relative h-[265px]" ...attributes>
      <SeriesColumn
        @data={{this.columns}}
        @categories={{this.months}}
        @stackingStyle="normal"
        @legend={{this.legend}}
        @yAxis={{this.yAxis}}
        @title={{@title}}
      />
    </div>
  </template>
}

export default DrivingTestsTotalsByMonth
